import { createFactory } from '@mfellner/valtio-factory';
import { constants } from '@pukka/common';
import { storage } from '@pukka/storage';
import { queryClient } from '@pukka/tanstack-query';
import axios from 'axios';
import { minutesToMilliseconds, isValid, differenceInMinutes, differenceInSeconds } from 'date-fns';
import Cookies, { CookieAttributes } from 'js-cookie';
import { devtools } from 'valtio/utils';
import { z } from 'zod';

const { GOID_SERVICE, MOKA_SERVICE_HOST, DEV, STORYBOOK_RUNTIME } = constants;

function setStorage(key: string, value?: string) {
  return typeof value === 'undefined' ? storage.remove(key) : storage.set(key, value);
}

function bulkRemoveStorage(
  toRemove: { name: string; options?: CookieAttributes }[],
  storeRemover = storage.remove,
  cookiesRemover = Cookies.remove,
  sessionStorageRemover = window?.sessionStorage ? window.sessionStorage.removeItem.bind(window.sessionStorage) : undefined,
) {
  toRemove.forEach(({ name, options }) => {
    storeRemover(name);
    cookiesRemover(name, options);
    sessionStorageRemover?.(name);
  });
}

const authTypeSchema = z.enum(['MokaAuth-username', 'GoAuth-email', 'GoID-otp', 'moka:authType'] as const).optional();

interface AuthState {
  accessToken?: string;
  refreshToken?: string;
  accessTokenExpiry?: string;
  authType: z.infer<typeof authTypeSchema>;
}

const renewAccessToken = z
  .function()
  .args(z.string().min(1), authTypeSchema)
  .implement(async (oldRefreshToken, authType) => {
    const renewTokenUrl =
      authType === 'GoAuth-email' ? `${GOID_SERVICE}/gobiz/goauth/oauth2/token` : `${MOKA_SERVICE_HOST}/account/v2/token`;
    const resp = await axios.post(
      renewTokenUrl,
      authType === 'GoAuth-email'
        ? {
            client_id: 'moka-pos-web',
            grant_type: 'refresh_token',
            refresh_token: oldRefreshToken,
          }
        : undefined,
      {
        headers: authType === 'GoAuth-email' ? { 'Authentication-Type': 'go-id' } : { Authorization: oldRefreshToken },
      },
    );

    const { access_token: accessToken, refresh_token: refreshToken, access_token_exp: accessTokenExpiry } = resp.data;

    return { accessToken, refreshToken, accessTokenExpiry };
  });

interface AuthContext {
  getAccessToken: typeof renewAccessToken;
}

const MOKA_TOKEN_STORAGE_NAME = 'moka:accessToken';
const MOKA_TOKEN_EXPIRY_STORAGE_NAME = 'moka:accessTokenExpiry';
const MOKA_REFRESH_TOKEN_STORAGE_NAME = 'moka:refreshToken';
const MOKA_AUTH_TYPE_STORAGE_NAME = 'moka:authType';

const authFactory = (initState: AuthState, context: AuthContext, shouldRedirect = true) =>
  createFactory<AuthState, AuthContext>(initState)
    .derived({
      expiry(state) {
        if (!state.accessToken || !state.accessTokenExpiry) {
          return new Date(-1);
        }

        const date = new Date(state.accessTokenExpiry);
        const validDate = isValid(date);

        return validDate ? date : new Date(-1);
      },
    })
    .actions({
      logout(redirectAfterLogin?: boolean, forceNotRedirect?: boolean) {
        this.accessToken = undefined;
        this.refreshToken = undefined;
        this.accessTokenExpiry = undefined;
        this.authType = undefined;

        storage.clearAll('user');

        bulkRemoveStorage([
          { name: 'hasSeenMokaOrderPromotion' },
          { name: 'response:user_role_name' },
          {
            name: 'remember_token',
            options: { path: '/', domain: '.mokapos.com' },
          },
          // Remove cookies
          // For GoFood Middleware UI
          {
            name: 'moka:authType',
            options: { domain: '.mokapos.com' },
          },
          {
            name: 'moka:accessToken',
            options: { domain: '.mokapos.com' },
          },
          { name: 'welcomeBannerDisplayed' },
        ]);

        if (forceNotRedirect) {
          return;
        }

        if (shouldRedirect) {
          if (redirectAfterLogin) {
            window.location.replace(`/login?next=${window.location.pathname}`);
          } else {
            window.location.replace('/login');
          }
        } else {
          console.warn("Should've navigate to /login");
        }
      },
    })
    .actions({
      expired() {
        // 10 secs as buffer
        return differenceInSeconds(this.expiry, new Date()) < 10;
      },
      stale() {
        // start refreshing in background 5 mins before expiry
        return differenceInMinutes(this.expiry, new Date()) < 5;
      },
    })
    .actions({
      async getAccessToken() {
        if (!this.stale()) {
          return this.accessToken;
        }

        if (!this.refreshToken) {
          if (this.expired()) {
            this.logout(true);
          }

          return this.accessToken;
        }

        const accessTokenPromise = this.$context
          .getAccessToken(this.refreshToken, this.authType)
          .then(({ accessToken, refreshToken, accessTokenExpiry }) => {
            this.accessToken = accessToken ?? this.accessToken;
            this.refreshToken = refreshToken ?? this.refreshToken;
            this.accessTokenExpiry = accessTokenExpiry ?? this.accessTokenExpiry;
          })
          .catch(() => {
            this.logout();
          });

        // wait only if expired. run in background if stale
        if (this.expired()) {
          await accessTokenPromise;

          if (this.expired()) {
            // logout if token is still expired
            this.logout();
          }
        }

        return this.accessToken;
      },
    })
    .subscribe((state) => {
      setStorage(MOKA_TOKEN_STORAGE_NAME, state.accessToken);
      setStorage(MOKA_REFRESH_TOKEN_STORAGE_NAME, state.refreshToken);
      setStorage(MOKA_TOKEN_EXPIRY_STORAGE_NAME, state.accessTokenExpiry);
      setStorage(MOKA_AUTH_TYPE_STORAGE_NAME, state.authType);
    })
    .onCreate((state) => devtools(state, { enabled: DEV, name: 'auth' }))
    .create(context);

function defaultAccessTokenGetter(refreshToken: string, authType: AuthState['authType']) {
  return queryClient.fetchQuery(['renewAccessToken'], renewAccessToken.bind(null, refreshToken, authType), {
    cacheTime: minutesToMilliseconds(2),
    staleTime: minutesToMilliseconds(1),
  });
}

const defaultAuthState = authFactory(
  {
    accessToken: storage.get(MOKA_TOKEN_STORAGE_NAME),
    refreshToken: storage.get(MOKA_REFRESH_TOKEN_STORAGE_NAME),
    accessTokenExpiry: storage.get(MOKA_TOKEN_EXPIRY_STORAGE_NAME),
    authType: storage.get(MOKA_AUTH_TYPE_STORAGE_NAME),
  },
  {
    getAccessToken: defaultAccessTokenGetter,
  },
  !STORYBOOK_RUNTIME,
);

export type { AuthState };

export {
  setStorage as _setStorage,
  bulkRemoveStorage as _bulkRemoveStorage,
  renewAccessToken as _renewAccessToken,
  authFactory as _authFactory,
  defaultAccessTokenGetter as _defaultAccessTokenGetter,
};

export default defaultAuthState;
